import { CallState } from "@b2bportal/core-types";
import {
  type ActivityBookableItem,
  type ActivityTimeSlotPrice,
  AgeBand,
  type AgeBandSelection,
  type ShopResults,
  ResponseEnum,
} from "@b2bportal/rbc-activities-api";
import {
  type CaseReducer,
  type PayloadAction,
  createSlice,
} from "@reduxjs/toolkit";
import { initialActivitiesShopState } from "./initialState";
import {
  fetchActivitiesShop,
  fetchActivitiesShopOption,
  fetchActivityReviews,
  type FetchActivityReviewsFulfillResponse,
  type FetchShopFulfillResponse,
} from "./thunk";
import type { ActivitiesShopState, ShopSearch } from "./types";

const setActivitiesShopOptionSuccess: CaseReducer<
  ActivitiesShopState,
  ReturnType<typeof fetchActivitiesShopOption.fulfilled>
> = (state, action) => {
  const { shopOption } = action.payload;
  const { request } = action.meta.arg;
  state.selectedDate = request.date;
  // Store the request params for deduplication
  state.lastShopOptionRequest = request;
  // Use optionId as key, or "default" if optionId is undefined
  const key = request.activityOptionId?.value ?? "default";
  if (action.meta.arg?.resetShopOptions) {
    state.shopOptions = {
      [key]: shopOption,
    };
  } else {
    state.shopOptions[key] = shopOption;
  }
  state.shopOptionCallState = CallState.Success;
};

export const activitiesShopSlice = createSlice({
  name: "activitiesShop",
  initialState: initialActivitiesShopState,
  reducers: {
    setShopSearch: (state, action: PayloadAction<ShopSearch>) => {
      state.shopSearch = action.payload;
    },
    setActivityUnavailable: (state, action: PayloadAction<boolean>) => {
      state.isActivityUnavailable = action.payload;
    },
    setActivity: (state, action: PayloadAction<ShopResults>) => {
      state.activity = action.payload;
      state.availabilityCallState = CallState.Success;
    },
    setSelectedParticipants: (
      state,
      action: PayloadAction<Array<AgeBandSelection>>
    ) => {
      state.selectedParticipants = action.payload;
    },
    setSelectedBookableItem: (
      state,
      action: PayloadAction<ActivityBookableItem>
    ) => {
      state.selectedBookableItem = action.payload;
      // Auto-select the first time slot onSelect of a bookableItem
      const timeSlots =
        action.payload.timeSlotsByDate?.[state.selectedDate] ?? [];
      state.selectedTimeSlot = timeSlots.length > 0 ? timeSlots[0] : undefined;
    },
    setSelectedTimeSlot: (
      state,
      action: PayloadAction<ActivityTimeSlotPrice>
    ) => {
      state.selectedTimeSlot = action.payload;
    },
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchActivitiesShop
      .addCase(fetchActivitiesShop.pending, (state) => {
        state.shopCallState = CallState.InProcess;
        // Reset shopOptions when fetching a new activity
        state.shopOptions = {};
        state.shopOptionCallState = CallState.NotCalled;
        state.lastShopOptionRequest = undefined;
      })
      .addCase(
        fetchActivitiesShop.fulfilled,
        (state, action: PayloadAction<FetchShopFulfillResponse>) => {
          state.shopSearch = action.payload.search;
          // Initialize selectedDate to match what the date picker shows
          state.selectedDate = action.payload.search.dateRange.from;

          const { shop } = action.payload;
          if (shop.Response === ResponseEnum.Success) {
            state.shop = shop;
            state.activity = shop.value;
            state.shopCallState = CallState.Success;

            const firstBookableItem = shop.value.bookableItems[0];
            state.selectedBookableItem = firstBookableItem;

            // Auto-select the first time slot for the selected date
            const timeSlots =
              firstBookableItem?.timeSlotsByDate?.[state.selectedDate] ?? [];
            state.selectedTimeSlot =
              timeSlots.length > 0 ? timeSlots[0] : undefined;

            // Initialize selectedParticipants with all ageBands from shop response
            // Traveler ageBand type is only returned for products with unit pricing (e.g., an ATV rental charged per vehicle or group rather than per person).
            const ageBandsFromShop = shop.value.ageBands ?? [];
            const uniqueAgeBandTypes = [
              ...new Set(ageBandsFromShop.map((ab) => ab.ageBand)),
            ];
            const hasTraveler = uniqueAgeBandTypes.includes(AgeBand.Traveler);

            // Create a map of ageBand -> minimum required travelers
            // Multiple entries per ageBand represent volume-based pricing tiers.
            // If any tier has minNumberOfTravelers=0, that means 0 is valid
            // and the age band is truly optional (no minimum required).
            const minTravelersMap = new Map<AgeBand, number>();
            for (const ageBand of ageBandsFromShop) {
              const minTravelers = ageBand.minNumberOfTravelers;
              if (minTravelers != null) {
                const currentMin = minTravelersMap.get(ageBand.ageBand);
                if (currentMin == null || minTravelers < currentMin) {
                  minTravelersMap.set(ageBand.ageBand, minTravelers);
                }
              }
            }

            state.selectedParticipants = uniqueAgeBandTypes.map(
              (ageBandType) => {
                const minTravelers = minTravelersMap.get(ageBandType);
                // Default to minimum required if specified, otherwise use legacy fallback
                const defaultCount =
                  minTravelers != null && minTravelers > 0
                    ? minTravelers
                    : ageBandType === AgeBand.Traveler
                      ? 1
                      : ageBandType === AgeBand.Adult && !hasTraveler
                        ? 1
                        : 0;

                return {
                  ageBand: ageBandType,
                  numberOfTravelers: defaultCount,
                };
              }
            );

            // Fallback: if no age bands returned from shop, default to Traveler
            if (uniqueAgeBandTypes.length === 0) {
              state.selectedParticipants.push({
                ageBand: AgeBand.Traveler,
                numberOfTravelers: 1,
              });
            }
          } else {
            state.isActivityUnavailable = true;
            state.shopCallState = CallState.Failed;
          }
        }
      )
      .addCase(fetchActivitiesShop.rejected, (state) => {
        state.shopCallState = CallState.Failed;
        state.isActivityUnavailable = true;
      })
      // Handle fetchActivitiesShopOption
      .addCase(fetchActivitiesShopOption.pending, (state, action) => {
        state.shopOptionCallState = CallState.InProcess;
        // Record the attempted request immediately so criteria-change effects
        // don't re-dispatch an identical request while it's in flight or after it fails
        state.lastShopOptionRequest = action.meta.arg.request;
      })
      .addCase(
        fetchActivitiesShopOption.fulfilled,
        setActivitiesShopOptionSuccess
      )
      .addCase(fetchActivitiesShopOption.rejected, (state) => {
        state.shopOptionCallState = CallState.Failed;
      })
      // Handle fetchActivityReviews
      .addCase(fetchActivityReviews.pending, (state) => {
        state.reviewsCallState = CallState.InProcess;
      })
      .addCase(
        fetchActivityReviews.fulfilled,
        (state, action: PayloadAction<FetchActivityReviewsFulfillResponse>) => {
          state.reviews = action.payload.reviews;
          state.reviewSummary = action.payload.reviewSummary;
          state.reviewsCallState = CallState.Success;
        }
      )
      .addCase(fetchActivityReviews.rejected, (state) => {
        state.reviewsCallState = CallState.Failed;
      });
  },
});

export const ActivitiesShopActions = activitiesShopSlice.actions;

export default activitiesShopSlice.reducer;
